import { z } from 'zod';
import { apiEnvelope } from './v1.js';

/**
 * Jarvis wire shapes (IDEA-62 WS-B / ADR 0085) — the plan model, the persisted
 * session-entry log, and the per-hop turn contract for the browser-driven loop.
 *
 * WHY here: plan shapes, entry schemas and hop wire shapes live ONCE, versioned, in
 * `@squadlab/contracts` (ADR 0085 §3) — the browser engine consumes them today through
 * the `JarvisBroker` port (scripted mock at B0), and the gateway parses the SAME
 * shapes when the live broker lands at B1. The browser sends deltas, never transcripts
 * (ADR 0085 §1); audit-relevant entries become gateway-authored on the live path.
 */

export const JARVIS_HTTP_CONTRACT_VERSION = 'jarvis-v1' as const;

/**
 * Effect classes gate tool execution (ADR 0085 §2): `read` auto-runs; `act` auto-runs
 * visibly; `spend` and `destructive` require pre-dispatch operator confirmation showing
 * the concrete payload. Confirmation is a gate, not a receipt.
 */
export const EffectClassSchema = z.enum(['read', 'act', 'spend', 'destructive']);
export type EffectClass = z.infer<typeof EffectClassSchema>;

/** One step of the model-maintained plan. `ask` steps pause the turn for operator input. */
export const JarvisPlanStepSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  kind: z.enum(['tool', 'ask', 'note']),
  status: z.enum(['pending', 'active', 'done', 'failed', 'skipped']),
  /** Tool id this step executes through (kind 'tool'). */
  tool: z.string().min(1).optional(),
  /** Step ids that must complete first. */
  dependsOn: z.array(z.string().min(1)).default([]),
  note: z.string().optional(),
});
export type JarvisPlanStep = z.infer<typeof JarvisPlanStepSchema>;

/** The plan is DATA (ADR 0085 §3): versioned, replayable, rendered — never implicit. */
export const JarvisPlanSchema = z.object({
  version: z.number().int().min(1),
  steps: z.array(JarvisPlanStepSchema),
});
export type JarvisPlan = z.infer<typeof JarvisPlanSchema>;

/**
 * The bounded-id charset+length invariant, exported for path/param validation (B1.5):
 * the sessions read routes carry a session id in the URL, and the gateway must refuse
 * an out-of-charset id at the boundary with the SAME rule the body schemas enforce —
 * one pattern, two seams, no drift.
 */
export const JARVIS_BOUNDED_ID_PATTERN = /^[A-Za-z0-9_.:-]{1,128}$/;

/**
 * Bounded identifier — ids cross the wire INTO persisted gateway rows on the live path
 * (B1), so every id-shaped field is length- AND charset-capped at the contract: a
 * multi-kilobyte or control-character "id" must fail parsing at the boundary, never
 * reach storage, a log line, or a prompt. The charset matches the gateway's
 * correlation-id invariant (ULIDs, UUIDs, `mock-*`, `toolu_*` all conform).
 */
const boundedId = z.string().regex(JARVIS_BOUNDED_ID_PATTERN, 'id contains illegal characters');

/**
 * The turn id doubles as the hop's COST correlation id on the live path (per-turn
 * rollups via /v1/costs/by-correlation), so it carries the gateway's exact
 * correlation charset + 64-char cap — a forged turnId must not be able to smuggle
 * log-forgery bytes into ledger rows the header-correlation guard already blocks.
 */
const turnIdSchema = z.string().regex(/^[A-Za-z0-9_.:-]{1,64}$/, 'turnId contains illegal characters');

/** Epoch-ms wall time: integer, non-negative, sane upper bound (year ~2100) — a
 *  client-authored `at` is cast to bigint in SQL, so an unbounded double must fail
 *  at the contract, not as a storage overflow. */
const epochMs = z.number().int().nonnegative().max(4_102_444_800_000);

/** A tool invocation the model requests; the engine executes it (confirm-gated by effect). */
export const JarvisToolCallSchema = z.object({
  callId: boundedId,
  toolId: boundedId,
  effect: EffectClassSchema,
  args: z.unknown(),
});
export type JarvisToolCall = z.infer<typeof JarvisToolCallSchema>;

/**
 * Every session entry carries the same envelope: a unique id, a per-session monotonic
 * sequence number (server-authored on the live path), the turn it belongs to, and epoch ms.
 */
const jarvisEntryBase = z.object({
  id: boundedId,
  seq: z.number().int().nonnegative(),
  turnId: turnIdSchema,
  at: epochMs,
});

export const JarvisUserMessageEntrySchema = jarvisEntryBase.extend({
  type: z.literal('user-message'),
  text: z.string(),
});
export type JarvisUserMessageEntry = z.infer<typeof JarvisUserMessageEntrySchema>;

export const JarvisAssistantMessageEntrySchema = jarvisEntryBase.extend({
  type: z.literal('assistant-message'),
  text: z.string(),
});
export type JarvisAssistantMessageEntry = z.infer<typeof JarvisAssistantMessageEntrySchema>;

/** One model hop's audit row — `simulated: true` + `costUsd: 0` on the mock path. */
export const JarvisModelCallEntrySchema = jarvisEntryBase.extend({
  type: z.literal('model-call'),
  model: z.string().min(1),
  inputTokens: z.number().int().nonnegative(),
  outputTokens: z.number().int().nonnegative(),
  costUsd: z.number().nonnegative(),
  durationMs: z.number().int().nonnegative(),
  simulated: z.boolean(),
  /**
   * Whether the broker considered the TURN finished after this hop (B2 / N65b —
   * additive, absent on pre-B2 rows). The gateway's server-side turn-id check reads
   * it back: a turn whose newest model-call carries `done: true` is COMPLETED, and a
   * later hop reusing its id is refused — the same-owner rollup-pollution channel.
   */
  done: z.boolean().optional(),
});
export type JarvisModelCallEntry = z.infer<typeof JarvisModelCallEntrySchema>;

export const JarvisToolCallEntrySchema = jarvisEntryBase.extend({
  type: z.literal('tool-call'),
  callId: z.string().min(1),
  toolId: z.string().min(1),
  effect: EffectClassSchema,
  args: z.unknown(),
});
export type JarvisToolCallEntry = z.infer<typeof JarvisToolCallEntrySchema>;

export const JarvisToolResultEntrySchema = jarvisEntryBase.extend({
  type: z.literal('tool-result'),
  callId: z.string().min(1),
  ok: z.boolean(),
  result: z.unknown().optional(),
  /** Classified failure (kernel ErrorKind vocabulary rides as a plain string on the wire). */
  error: z.object({ kind: z.string().min(1), message: z.string() }).optional(),
});
export type JarvisToolResultEntry = z.infer<typeof JarvisToolResultEntrySchema>;

export const JarvisPlanUpdateEntrySchema = jarvisEntryBase.extend({
  type: z.literal('plan-update'),
  plan: JarvisPlanSchema,
});
export type JarvisPlanUpdateEntry = z.infer<typeof JarvisPlanUpdateEntrySchema>;

/** A first-class question to the operator — pauses the turn (plan `ask` steps render it). */
export const JarvisAskEntrySchema = jarvisEntryBase.extend({
  type: z.literal('ask'),
  question: z.string().min(1),
  options: z.array(z.string()).optional(),
});
export type JarvisAskEntry = z.infer<typeof JarvisAskEntrySchema>;

/** The operator (or teardown) aborted the turn; the plan is kept for resume/revise. */
export const JarvisInterruptedEntrySchema = jarvisEntryBase.extend({
  type: z.literal('interrupted'),
});
export type JarvisInterruptedEntry = z.infer<typeof JarvisInterruptedEntrySchema>;

/**
 * Widget kind charset (IDEA-83 increment 2 / ADR 0100 pillar 1): a bounded, lowercase dotted/
 * hyphenated token. It keys the widget registry in `@squadlab/jarvis` AND rides as a CSS/`data-*`
 * hook, so it must be a safe, attribute-legal id — a multi-kilobyte or markup-bearing "kind" must
 * fail parsing at the boundary, never reach a class name or a registry key.
 */
export const JARVIS_WIDGET_KIND_PATTERN = /^[a-z][a-z0-9.-]*$/;
const widgetKind = z.string().min(1).max(64).regex(JARVIS_WIDGET_KIND_PATTERN, 'widget kind contains illegal characters');

/** Max action buttons ONE widget entry may carry (host-authored; bounded for display + DoS). */
export const JARVIS_WIDGET_ENTRY_MAX_ACTIONS = 8;
/** Length cap for a widget action's UNTRUSTED display label (rendered via `textContent`, never markup). */
export const JARVIS_WIDGET_ACTION_LABEL_MAX_CHARS = 200;
/** Length cap for a widget action's command name (resolved THROUGH the client capability registry). */
export const JARVIS_WIDGET_ACTION_COMMAND_MAX_CHARS = 120;

/**
 * A declarative widget ACTION carried by a widget ENTRY (IDEA-83 Part C / ADR 0100 pillar 4). A HOST
 * tool (e.g. the idea-engine's `ui.show.idea`) attaches these when it EMITS a widget, so the dock
 * renders each as a gated `<button>`. Carried ON the entry (not derived from the renderer) so the
 * core renderer stays DOMAIN-FREE — the card + its action buttons render in EVERY service's Jarvis,
 * and the action rides the persisted entry so a cross-service re-render can offer (or, per Part C's
 * registry-conditional filter, hide) it.
 *
 * SECURITY (the trust boundary is unchanged): `label` is UNTRUSTED display text and CANNOT lie about
 * the effect — the `command` is resolved through the CLIENT capability registry (never this
 * descriptor, never a wire hint), and its `payload` is Zod-parsed by that command's own schema
 * before anything runs. A widget can only EMIT this descriptor; the operator's activation then goes
 * through the gate (resolve → parse → classify → park|dispatch|refuse).
 */
export const JarvisWidgetActionSchema = z.object({
  /** UNTRUSTED display text for the control (rendered via `textContent`; bounded for display + DoS). */
  label: z.string().max(JARVIS_WIDGET_ACTION_LABEL_MAX_CHARS),
  /** The command to resolve THROUGH THE CLIENT REGISTRY (never trusted as an effect). */
  command: z.string().min(1).max(JARVIS_WIDGET_ACTION_COMMAND_MAX_CHARS),
  /** The command's payload — Zod-parsed by the command's own schema at the gate (parse, don't validate). */
  payload: z.record(z.unknown()),
});
export type JarvisWidgetAction = z.infer<typeof JarvisWidgetActionSchema>;

/**
 * A typed, service-agnostic DISPLAY block (IDEA-83 increment 2 / ADR 0100 pillar 1) — the seam for
 * neat, purpose-built views inline in the transcript (tables, key-value, code, sparklines; a
 * service registers its own kinds). ADDITIVE + versioned: no existing variant changes and existing
 * consumers keep working. `kind` keys the widget registry (charset-restricted above). `data` is
 * `unknown` ON THE WIRE and is Zod-parsed by the WIDGET'S OWN schema at render time (parse, don't
 * validate — ADR 0005); the entry schema deliberately does NOT validate `data`'s shape, and a
 * render-time parse failure shows a visible fallback, never a partial view. `actions` (IDEA-83 Part
 * C) are OPTIONAL host-authored action buttons the dock renders beside the card — see
 * {@link JarvisWidgetActionSchema}.
 *
 * PROVENANCE: widgets are authored by the assistant/tools SERVER-SIDE and arrive as persisted /
 * gateway-authored transcript entries — never a browser delta. `widget` is intentionally absent
 * from the gateway's browser-authorable set (`JARVIS_BROWSER_DELTA_TYPES`), so a browser delta of
 * type `widget` is refused at the hop boundary (ADR 0085 §4). Schema-legal as an entry ≠ browser-
 * authorable.
 */
export const JarvisWidgetEntrySchema = jarvisEntryBase.extend({
  type: z.literal('widget'),
  kind: widgetKind,
  data: z.unknown(),
  actions: z.array(JarvisWidgetActionSchema).max(JARVIS_WIDGET_ENTRY_MAX_ACTIONS).optional(),
});
export type JarvisWidgetEntry = z.infer<typeof JarvisWidgetEntrySchema>;

export const JarvisEntrySchema = z.discriminatedUnion('type', [
  JarvisUserMessageEntrySchema,
  JarvisAssistantMessageEntrySchema,
  JarvisModelCallEntrySchema,
  JarvisToolCallEntrySchema,
  JarvisToolResultEntrySchema,
  JarvisPlanUpdateEntrySchema,
  JarvisAskEntrySchema,
  JarvisInterruptedEntrySchema,
  JarvisWidgetEntrySchema,
]);
export type JarvisEntry = z.infer<typeof JarvisEntrySchema>;

/**
 * One hop of the browser-driven turn loop (ADR 0085 §1): the engine sends only the
 * DELTAS since the last hop (the gateway reloads persisted session state per call —
 * no in-memory server sessions), and receives new entries, tool calls to execute,
 * an optional plan revision, and whether the turn is done.
 */
export const JarvisHopRequestSchema = z
  .object({
    sessionId: boundedId,
    turnId: turnIdSchema,
    // Deltas are BOUNDED at the contract (abuse guard): a hop carries one user message
    // and/or a handful of tool results — never a transcript (the gateway holds those).
    // Ids must be unique within a request: entry ids are durable storage keys, so an
    // in-request duplicate is a client bug surfaced as a 400, never a storage error.
    deltas: z
      .array(JarvisEntrySchema)
      .max(32)
      .refine((ds) => new Set(ds.map((d) => d.id)).size === ds.length, { message: 'duplicate delta ids' }),
  })
  // B2 (N65b): every delta must belong to THE hop's turn — a delta stamped with an
  // older turn id would splice client content into that turn's persisted audit trail
  // under a fresh hop turnId, sidestepping the server turn check. The engine always
  // stamps deltas with the current turn, so honest clients never hit this.
  .refine((req) => req.deltas.every((d) => d.turnId === req.turnId), {
    message: 'delta turnId must match the hop turnId',
    path: ['deltas'],
  });
export type JarvisHopRequest = z.infer<typeof JarvisHopRequestSchema>;

export const JarvisHopResponseSchema = z.object({
  entries: z.array(JarvisEntrySchema),
  toolCalls: z.array(JarvisToolCallSchema).default([]),
  plan: JarvisPlanSchema.optional(),
  done: z.boolean(),
  usage: z.object({
    inputTokens: z.number().int().nonnegative(),
    outputTokens: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative(),
    simulated: z.boolean(),
  }),
});
export type JarvisHopResponse = z.infer<typeof JarvisHopResponseSchema>;

// ── Streaming frames (B4 / IDEA-77, ADR 0089) ──────────────────────────────────
//
// `POST /v1/jarvis/hop/stream` carries a discriminated union on `type`: a stream is
// `delta`* then EXACTLY ONE terminal (`result` | `error`). Genuinely additive — the
// buffered `JarvisHopResponse` above is unchanged, and the `result` frame REUSES it.

/**
 * One incremental assistant-text chunk on the streaming channel (ADR 0089 §2) — the
 * live-typing payload the dock renders as it arrives. Zero or more per hop; carries the
 * hop's turn id so a frame can be attributed. `text` is unbounded on purpose (the model
 * authors it; the persisted assistant-message entry in the terminal `result` frame is the
 * audited copy — a delta is transient render fuel, never stored).
 */
export const JarvisHopDeltaFrameSchema = z.object({
  type: z.literal('delta'),
  turnId: turnIdSchema,
  text: z.string(),
});
export type JarvisHopDeltaFrame = z.infer<typeof JarvisHopDeltaFrameSchema>;

/**
 * The TERMINAL success frame (ADR 0089 §2): its payload is EXACTLY a {@link JarvisHopResponse}
 * with a `type: 'result'` tag added, so the browser engine reuses its existing terminal
 * handling verbatim (append entries incl. the model-call ledger row, setPlan, dispatch
 * toolCalls, act on done) and back-compat is free.
 */
export const JarvisHopResultFrameSchema = JarvisHopResponseSchema.extend({
  type: z.literal('result'),
});
export type JarvisHopResultFrame = z.infer<typeof JarvisHopResultFrameSchema>;

/**
 * The TERMINAL error/abort frame (ADR 0089 §2/§4): any non-success terminal — a mid-stream
 * cancel, a model/timeout/provider failure — ends the stream as ONE error frame, never a
 * bare mid-stream HTTP error. `kind` rides the kernel ErrorKind vocabulary as a plain wire
 * string (same idiom as the tool-result error).
 */
export const JarvisHopErrorFrameSchema = z.object({
  type: z.literal('error'),
  error: z.object({ kind: z.string().min(1), message: z.string() }),
});
export type JarvisHopErrorFrame = z.infer<typeof JarvisHopErrorFrameSchema>;

/**
 * One frame of `POST /v1/jarvis/hop/stream` (B4 / ADR 0089). The web api-broker parses
 * EACH inbound frame with this schema at the boundary (parse, don't validate — ADR 0005)
 * before the engine consumes it: `delta` → the live buffer, `result` → the existing
 * terminal handling, `error` → a visible failure.
 */
export const JarvisHopFrameSchema = z.discriminatedUnion('type', [
  JarvisHopDeltaFrameSchema,
  JarvisHopResultFrameSchema,
  JarvisHopErrorFrameSchema,
]);
export type JarvisHopFrame = z.infer<typeof JarvisHopFrameSchema>;

/**
 * The `{data,error,meta}` wire envelope `POST /v1/jarvis/hop` responds with (B1) —
 * what the web api-broker adapter parses (same idiom as every `/v1/*` sibling).
 */
export const JarvisHopEnvelopeSchema = apiEnvelope(JarvisHopResponseSchema);
export type JarvisHopEnvelope = z.infer<typeof JarvisHopEnvelopeSchema>;

// ── Past sessions (B1.5 / IDEA-71) — list with costs + entries read for hydration ──

/** Server cap on `GET /v1/jarvis/sessions` (newest-first by `updatedAtMs`). */
export const JARVIS_SESSIONS_LIST_LIMIT = 50;

/** Default + max window for `GET /v1/jarvis/sessions/:id/entries` (hydration read). */
export const JARVIS_SESSION_ENTRIES_DEFAULT_LIMIT = 40;
export const JARVIS_SESSION_ENTRIES_MAX_LIMIT = 100;

/**
 * One owner-scoped session summary. Cost/count are AGGREGATES over the session's
 * persisted entries (`model-call` rows' `costUsd`; all-rows count) — the gateway
 * derives them, the browser only renders. `title` stays null until B5's auto-title
 * lands; bounded so a future writer can never ship an unbounded header row.
 */
export const JarvisSessionSummarySchema = z.object({
  id: boundedId,
  title: z.string().max(200).nullable(),
  createdAtMs: epochMs,
  updatedAtMs: epochMs,
  entryCount: z.number().int().nonnegative(),
  totalCostUsd: z.number().nonnegative(),
});
export type JarvisSessionSummary = z.infer<typeof JarvisSessionSummarySchema>;

/** `GET /v1/jarvis/sessions` — the owner's sessions, newest-first, capped at 50. */
export const JarvisSessionsResponseSchema = z.object({
  sessions: z.array(JarvisSessionSummarySchema).max(JARVIS_SESSIONS_LIST_LIMIT),
});
export type JarvisSessionsResponse = z.infer<typeof JarvisSessionsResponseSchema>;

export const JarvisSessionsEnvelopeSchema = apiEnvelope(JarvisSessionsResponseSchema);
export type JarvisSessionsEnvelope = z.infer<typeof JarvisSessionsEnvelopeSchema>;

/**
 * `GET /v1/jarvis/sessions/:id/entries` — the newest `limit` entries of ONE owned
 * session in transcript order (oldest→newest), the smallest honest hydration read:
 * the same window the hop-side prompt reloads, now readable by the browser so a
 * past session can be rendered and CONTINUED (the next hop appends server-side).
 * `entries` is capped at the route's own max window (PR-10) — the sibling sessions
 * array is capped, and an unbounded entries payload must fail the same way instead
 * of riding into the hydration parse.
 */
export const JarvisSessionEntriesResponseSchema = z.object({
  sessionId: boundedId,
  entries: z.array(JarvisEntrySchema).max(JARVIS_SESSION_ENTRIES_MAX_LIMIT),
});
export type JarvisSessionEntriesResponse = z.infer<typeof JarvisSessionEntriesResponseSchema>;

export const JarvisSessionEntriesEnvelopeSchema = apiEnvelope(JarvisSessionEntriesResponseSchema);
export type JarvisSessionEntriesEnvelope = z.infer<typeof JarvisSessionEntriesEnvelopeSchema>;

/** Success payload for `DELETE /v1/jarvis/sessions/:id` (B5 / IDEA-80): the deleted session id. */
export const JarvisSessionDeleteResponseSchema = z.object({ sessionId: boundedId });
export type JarvisSessionDeleteResponse = z.infer<typeof JarvisSessionDeleteResponseSchema>;

export const JarvisSessionDeleteEnvelopeSchema = apiEnvelope(JarvisSessionDeleteResponseSchema);
export type JarvisSessionDeleteEnvelope = z.infer<typeof JarvisSessionDeleteEnvelopeSchema>;
